import React, { lazy, Suspense } from 'react';
import { Opportunity, OpportunityDetection } from "@/opportunity/opportunity";
import { 
  getFailurePatternChartConfig, 
  shouldShowFailurePatternCharts 
} from './failurePatternChartConfig';

// Lazy load chart components to avoid circular dependencies and improve bundle size
const OpportunityAthenaChartCardWrapper = lazy(() => import('./OpportunityAthenaChartCardWrapper'));
const OpportunityShortCyclingCard = lazy(() => import('./OpportunityShortCyclingCard'));
const FrequencySeverityOverTimeCard = lazy(() => import('./FrequencySeverityOverTimeCard'));

// Component registry - map component names to their lazy-loaded components
const COMPONENT_REGISTRY: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  'OpportunityCompressorTempCard': OpportunityAthenaChartCardWrapper,
  'OpportunityFreezingSuctionCard': OpportunityAthenaChartCardWrapper,
  'OpportunityShortCyclingCard': OpportunityShortCyclingCard,
  'FrequencySeverityOverTimeCard': FrequencySeverityOverTimeCard,
  // Add more components here as you create them:
  // 'OpportunityCustomChart': lazy(() => import('./OpportunityCustomChart')),
};

interface OpportunityFailurePatternChartsProps {
  opportunity: Opportunity;
  currentDetection?: OpportunityDetection | null;
  onLivePlotSuccess?: (hasData: boolean) => void;
}

/**
 * Generic component that renders failure pattern-specific charts
 * based on the opportunity's detector_key (failure pattern ID)
 * 
 * To add charts for a new failure pattern:
 * 1. Create your chart component
 * 2. Add it to the COMPONENT_REGISTRY above
 * 3. Add a configuration entry in failurePatternChartConfig.ts
 */
const OpportunityFailurePatternCharts: React.FC<OpportunityFailurePatternChartsProps> = ({ 
  opportunity,
  currentDetection,
  onLivePlotSuccess
}) => {
  // Check if we should show charts for this opportunity
  if (!shouldShowFailurePatternCharts(opportunity)) {
    return null;
  }

  const failurePatternId = (opportunity.category_object as any)?.detector_key;
  const config = getFailurePatternChartConfig(failurePatternId);

  if (!config || !config.charts || config.charts.length === 0) {
    return null;
  }

  // Render all configured charts for this failure pattern
  return (
    <>
      {config.charts.map((chartConfig, index) => {
        const ChartComponent = COMPONENT_REGISTRY[chartConfig.componentName];
        
        if (!ChartComponent) {
          console.warn(`Chart component "${chartConfig.componentName}" not found in registry`);
          return null;
        }

        return (
          <Suspense key={`${failurePatternId}-chart-${index}`} fallback={<div>Loading chart...</div>}>
            <ChartComponent
              opportunity={opportunity}
              currentDetection={currentDetection}
              onLivePlotSuccess={onLivePlotSuccess}
              {...(chartConfig.props || {})}
            />
          </Suspense>
        );
      })}
    </>
  );
};

export default OpportunityFailurePatternCharts;

